import * as _ from 'lodash';
import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { CsvHelper, FileHelper, ParserHelper } from 'src/app/helpers';
import { Property } from 'src/app/models';
import { CsvBool, CsvSelectOne, CsvPropertyData } from './upload-properties-dialog.viewmodel';

type ValidationError = string;
type ValidationErrors = ValidationError[];

type CsvPropertyFields = Partial<Property>;

type Mapping<T> = { col: T, map: (value: string) => any, required?: boolean, pattern?: RegExp };
type MappingColumn = Mapping<keyof CsvPropertyData>;

type Parsed<T> = KeysAs<T, any>;
type ParsedPropertyFields = Parsed<CsvPropertyFields>;

const v = ParserHelper;
const toCsvBool = (val: string) => {
  switch (v.toEnumValue(CsvBool, val)) {
    case CsvBool.Yes: return true;
    case CsvBool.No: return false;
    default: return undefined;
  }
};

@Component({
  selector: 'app-upload-properties-dialog',
  templateUrl: './upload-properties-dialog.component.html',
  styleUrls: ['./upload-properties-dialog.component.css']
})
export class UploadPropertiesDialogComponent implements OnInit {
  readonly TEMPLATE = 'properties_to_upload_template.xls';

  downloadedFile = false;
  selectedFile: File|null = null;

  summary = {
    rows:       0,
    parsed:     0,
    invalid:    0,
    errors:     0,
    duplicates: 0,
  };

  parsed: ParsedPropertyFields[] = [];
  duplicates: string[] = []; // emails

  error: string = '';
  errors: string[] = [];

  // see Excel template: properties_to_upload_template.xls
  readonly propertyMapping: KeysAs<CsvPropertyFields, MappingColumn|null> = {
    isAvailable          : { col: 'For Rent *', map                   : toCsvBool,  required: true },
    ownerName            : { col: 'Owner Name *', map                 : v.toString, required: true },
    managerName          : { col: 'Manager Name *', map               : v.toString, required: true },
    contactPhone         : { col: 'Contact Phone # *', map            : v.toString, required: true, pattern: /^\+?[( )\d]+$/g },
    email                : { col: 'Email *', map                      : v.toString, required: true },
    address              : { col: 'Address *', map                    : v.toString, required: true },
    city                 : { col: 'City *', map                       : v.toString, required: true },
    zipCode              : { col: 'Zip Code *', map                   : v.toString, required: true },
    buildingNumber       : { col: 'Building #', map                   : v.toString },
    buildingUnits        : { col: '# of Units in Building', map       : v.toInt },
    availableUnits       : { col: 'Available Units', map              : v.toInt },
    propertyNote         : { col: 'Property Note', map                : v.toString },
    hcvCheck             : { col: 'Housing Vouchers Acceptance *', map: toCsvBool,  required: true },
    hcvNote              : { col: 'Voucher Note', map                 : v.toString },
    buildingAdaAccessible: { col: 'Building Accessible *', map        : toCsvBool,  required: true },
    unitAdaAccessible    : { col: 'Unit Accessible *', map            : toCsvBool,  required: true },
    accessibleNote       : { col: 'Accessible Note', map              : v.toString },
    incentiveForRentwise : { col: 'Incentive for Rentwise *', map     : toCsvBool,  required: true },
    incentiveNote        : { col: 'Incentive Note', map               : v.toString },
  };

  readonly propertyMappingFields = Object.keys(this.propertyMapping) as Array<keyof CsvPropertyFields>;
  readonly propertyMappingCols = Object.values(this.propertyMapping).map(m => m?.col as string).filter(col => col);

  constructor(
    private dialogRef: MatDialogRef<UploadPropertiesDialogComponent>,
    @Inject(MAT_DIALOG_DATA) data: any,
    private cd: ChangeDetectorRef
  ) { }

  ngOnInit(): void {
    console.log('init upload');
  }

  get enableUpload(): boolean {
    return Boolean(
      this.selectedFile &&
      this.summary.parsed > 0 &&
      this.summary.invalid === 0 &&
      this.summary.errors === 0
    );
  }

  async downloadTemplate() {
    const xlsBytes = await fetch("../../../../../assets/" + this.TEMPLATE).then(res => res.arrayBuffer());
    FileHelper.saveByteArray(this.TEMPLATE, xlsBytes, 'application/vnd.ms-excel');
    this.downloadedFile = true;
  }

  async loadPropertiesFrom(target: EventTarget|null) {
    const input = target as HTMLInputElement;
    const file = input?.files ? input.files.item(0) : null;

    this.selectedFile = file;

    // 0. Reset
    this.resetData();
    this.resetSelectedFile(input);
    this.resetSummary();

    if (!file) {
      return;
    }

    // 1. Parse CSV
    const data = await this.parseCsv(file);

    if (!data) {
      this.summary.errors++;
      console.log('failed to parse csv file', file.name);
      return;
    }

    this.summary.rows = data.length;

    // 2. Validate schema
    const errors = this.validateSchema(data);

    if (errors.length) {
      this.errors = _.uniq(errors);
      this.summary.errors = this.errors.length;
      return;
    }

    // 3. Map row to ParsedProperty, skip empty rows
    const parsed = data
      .filter(row => this.isEmptyRow(row) === false) // skip empty rows
      .map(row => this.mapPropertyFrom(row)) // row -> property
      .filter(data => this.isEmptyProperty(data) === false); // skip empty properties (no data entered, only "-- select one --" placeholders left)

    this.parsed = parsed;
    this.summary.parsed = parsed.length;

    // 4. Validate each ParsedProperty
    parsed.forEach(property => {
      const errors = this.validateEach(property);

      if (errors.length) {
        this.summary.invalid++; // row
        this.errors.push(...errors);
      }
    });

    // 5. Validate duplicates in CSV by email address
    parsed.forEach((property, index) => {
      const next = parsed.slice(index + 1);
      const errors = this.validateDuplicates(next, property);

      if (errors.length) {
        this.duplicates.push(property.email);
      }
    });

    // 6. Collect duplicates
    if (this.duplicates.length) {
      this.duplicates = _.uniq(this.duplicates);
      this.summary.duplicates = this.duplicates.length;
    }

    // 7. Collect errors
    if (this.errors.length) {
      this.errors = _.uniq(this.errors);
      this.summary.errors = this.errors.length;
    }
  }

  upload() {
    if (!this.enableUpload) {
      return;
    }

    this.dialogRef.close({ properties: this.parsed });
  }

  close() {
    this.dialogRef.close();
  }

  private async parseCsv(file: File) {
    const data = await CsvHelper.parseFromFile<CsvPropertyData>(file).catch(error => {
      if (error.length) {
        this.error = error[0].message || 'csv format error';
      }
      else if (error.message) {
        this.error = error.message;
      }
      else {
        this.error = 'csv format error';
      }
    });

    this.cd.markForCheck();

    return data;
  }

  private validateSchema(data: CsvPropertyData[]): ValidationErrors {
    const errors = [] as ValidationErrors;

    if (!data.length) {
      errors.push('No properties found in CSV file.');
    }

    if (data.length) {
      this.errors.push(...this.validateColumns(data));
    }

    return errors;
  }

  private validateColumns(data: CsvPropertyData[]): ValidationErrors {
      const cols = Object.keys(data[0]);
      const expected = this.propertyMappingCols;
      const errors = expected.filter(e => !cols.includes(e)).map(col => `${col} column is missing.`);
      return errors;
  }

  private validateEach(property: ParsedPropertyFields): ValidationErrors {
    const errors = [] as ValidationErrors;

    for (const field of this.propertyMappingFields) {
      const mapping = this.propertyMapping[field];
      const value = property[field];

      if (value && value.$error) {
        errors.push(value.$error);
        continue;
      }

      if (!mapping) {
        continue;
      }

      if (mapping.required && (value === CsvSelectOne || this.isEmpty(value))) {
        let col = String(mapping.col);

        // trim " *" at the end for mandatory fields
        if (col.endsWith(' *')) {
          col = col.substring(0, col.length - 2);
        }

        errors.push(`${col} is required.`);
      }
    }

    return errors;
  }

  private validateDuplicates(next: ParsedPropertyFields[], property: ParsedPropertyFields): ValidationErrors {
    if (!next.length) {
      return [];
    }

    const errors: ValidationErrors = [];

    if (property.email && next.some(u => u.email.toLowerCase() === property.email.toLowerCase())) {
      errors.push(`Found duplicates for email: ${property.email}`);
    }

    return errors;
  }

  private mapPropertyFrom(row: CsvPropertyData): ParsedPropertyFields {
    const property = {} as any;

    for (const field of this.propertyMappingFields) {
      const mapping: MappingColumn = (this.propertyMapping as any)[field];

      if (mapping) {
        const raw = row[mapping.col];
        const map = mapping.map;
        const val = map(raw);

        let value = map === v.toString ? val.trim() : val;

        if (mapping.pattern) {
          value = (String(value).match(mapping.pattern) || []).join('');
          mapping.pattern.lastIndex = 0; // reset regex for next use
        }

        property[field] = value !== CsvSelectOne ? value : null;

        const invalid = raw && raw !== CsvSelectOne && this.isEmpty(value);

        if (invalid) {
          if (map === v.toDate) {
            property[field] = { $error: `Invalid date format for ${mapping.col}: "${raw}"` };
          }
          else {
            property[field] = { $error: `Invalid value for ${mapping.col}: "${raw}"` };
          }
        }
      }
    }

    return property;
  }

  private isEmpty(value: unknown): boolean {
    return value === null || value === undefined || (typeof value === 'string' ? value.trim() === '' : false);
  }

  private isEmptyRow(data: CsvPropertyData): boolean {
    return Object.keys(data).every(col => this.isEmpty(data[col]));
  }

  private isEmptyProperty(property: ParsedPropertyFields): boolean {
    for (const field of this.propertyMappingFields) {
      const mapping = this.propertyMapping[field];
      const value = property[field];

      if (!mapping) {
        continue;
      }

      if (value && value !== CsvSelectOne && !this.isEmpty(value)) {
        return false;
      }
      else {
        continue;
      }
    }

    return true;
  }

  private resetData() {
    this.error = '';
    this.errors = [];
    this.parsed = [];
    this.duplicates = [];
  }

  private resetSelectedFile(input: HTMLInputElement) {
    input.value = '';
  }

  private resetSummary() {
    this.summary = {
      rows:       0,
      parsed:     0,
      invalid:    0,
      errors:     0,
      duplicates: 0,
    };
  }
}
